/**
 * Page request schemas — shared between the API (server-side request
 * validation) and the web SPA (client-side form validation).
 *
 * Spec §6 Page, §19 Page, §19 PageVersion, §20 blocksOrderJson Shape (MVP):
 *   - A `Page` is the URL slot inside a `Site` and is identified by a `path`
 *     (e.g. `/`, `/contact`, `/blog/how-to-x`). The active form of the
 *     `(siteId, path)` pair is unique via the partial index
 *     `page_site_path_active_unique` on `WHERE deletedAt IS NULL` (see
 *     `prisma/migrations/0001_init/migration.sql`). We never declare
 *     `@unique` in the Prisma schema for `(siteId, path)`, so all path lookups
 *     go through `findFirst({ where: { siteId, path, deletedAt: null } })`.
 *   - Path normalisation (lowercase + trim) happens in the service layer —
 *     mirrors the slug rule from `sites.ts`. The schema enforces structural
 *     constraints; the service applies the canonicalising transform before
 *     persisting / comparing.
 *   - Creating a Page also creates `PageVersion` 1 (status = DRAFT,
 *     versionNumber = 1, blocksOrderJson with empty `blocks`) inside the
 *     same `prisma.$transaction` so partial state cannot exist (spec §7,
 *     §19 Page Lifecycle Summary).
 *
 * Path regex rationale:
 *   - The platform spec models page paths as URL paths within a site. The
 *     issue brief constrains the allowed characters to `[a-z0-9/-]` because
 *     anything broader (`%`, `?`, `#`, encoded sequences) is reserved for
 *     other URL components — query strings and fragments belong on the
 *     request, not the storage row. Restricting to lowercase ASCII alnum + `/`
 *     + `-` keeps URL canonicalisation trivial: there is exactly one storage
 *     form for a given path, regardless of how the operator typed it.
 *   - The path MUST start with `/` so we never store relative-looking paths
 *     and the renderer can join site-base + page-path with a single slash
 *     rule.
 *   - The path MUST NOT end with a trailing slash UNLESS it is the bare
 *     root path `/`. Trailing slashes on non-root paths produce duplicate
 *     URLs (`/foo` vs `/foo/`) which would let an operator accidentally
 *     create two pages that resolve to the same canonical URL — the index
 *     would then surface one or the other based on insertion order, which
 *     is not predictable.
 *   - Multi-level paths like `/blog/how-to-x` are allowed; we don't restrict
 *     the depth at MVP. We do, however, reject empty path segments
 *     (`/foo//bar`) because they're rendered the same as `/foo/bar` by every
 *     client and would produce the same surprise as trailing slashes.
 *   - Maximum length is 256 — generous enough for SEO-friendly slugs (the
 *     RFC 3986 hard cap is far higher) but capped to keep index size bounded
 *     and to head off pathological inputs.
 *
 * The composed regex:
 *   - exactly `/`, OR
 *   - `/` + one-or-more (`[a-z0-9-]+`) segments separated by single `/`
 *     (no `//`, no leading `-` per-segment is *not* enforced — single-letter
 *     and hyphenated segments are both valid as long as the segment is
 *     non-empty).
 */

import { z } from "zod";
import type {
  BlockType,
  ContentItemType,
  ContentStatus,
  ExperimentStatus,
  PageType,
} from "../types/index.js";
import { blocksOrderJson, type BlocksOrderJson } from "./json.js";

/**
 * Path regex: `^/$|^/[a-z0-9-]+(?:/[a-z0-9-]+)*$`
 *   - first alt matches the bare root path `/`,
 *   - second alt matches `/segment(/segment)*` where each segment is one or
 *     more `[a-z0-9-]` chars.
 *
 * Anchored at both ends. Does not permit trailing slash on non-root paths.
 */
export const PAGE_PATH_REGEX = /^\/$|^\/[a-z0-9-]+(?:\/[a-z0-9-]+)*$/;

export const pagePathSchema = z
  .string()
  .min(1, "Path is required")
  .max(256, "Path must be at most 256 characters")
  .regex(
    PAGE_PATH_REGEX,
    "Path must start with /, contain only lowercase letters, digits, hyphens, and / separators, and not end with a trailing slash unless it is the bare root path",
  );

/**
 * Page type enum — mirror of `PageType` in `shared/src/types/index.ts`.
 * Centralised here (instead of importing the type-only union) so Zod can
 * enumerate the literal values at runtime for `.enum()`.
 */
export const pageTypeSchema = z.enum([
  "HOMEPAGE",
  "CONTACT",
  "BLOG_INDEX",
  "BLOG_ARTICLE",
  "PRODUCT_INDEX",
  "PRODUCT_DETAIL",
  "LANDING",
  "CUSTOM",
]) satisfies z.ZodType<PageType>;

/**
 * Body schema for `POST /api/admin/sites/:siteId/pages`.
 *
 * `.strict()` rejects unknown top-level keys so a typo like `Path` (capital P)
 * surfaces as a 422 instead of being silently dropped.
 */
export const createPageSchema = z
  .object({
    path: pagePathSchema,
    type: pageTypeSchema,
    title: z.string().trim().min(1, "Title is required").max(200),
  })
  .strict();
export type CreatePageInput = z.infer<typeof createPageSchema>;

/**
 * Body schema for `PATCH /api/admin/pages/:id`. Only `path` and `type` are
 * mutable on the Page identity row at MVP — title, blocks, SEO, etc. live on
 * `PageVersion` (see spec §19 Page).
 */
export const updatePageSchema = z
  .object({
    path: pagePathSchema.optional(),
    type: pageTypeSchema.optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  });
export type UpdatePageInput = z.infer<typeof updatePageSchema>;

/**
 * Query-parameter schema for `GET /api/admin/sites/:siteId/pages`. Standard
 * offset pagination per `standards/api-patterns.md` (default 20, max 100).
 */
export const listPagesQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type ListPagesQuery = z.infer<typeof listPagesQuerySchema>;

/**
 * Public-facing Page shape. ISO-string timestamps for transport.
 */
export type PageResponse = {
  id: string;
  siteId: string;
  path: string;
  type: PageType;
  createdAt: string;
  updatedAt: string;
};

/**
 * Backlink reference from a PageVersion / ContentVersion to the experiments
 * that reference it (issue #255 AC1).
 *
 * EXPERIMENT-status versions are pointed at by `ExperimentVariant` rows —
 * either directly via `ExperimentVariant.pageVersionId` (PAGE_VERSION
 * experiments) or via `ExperimentVariant.contentOverridesJson.overrides[]`
 * (CONTENT_OVERRIDES experiments). The admin detail surface needs to render
 * these inline under each EXPERIMENT row so operators can see at a glance
 * which experiment(s) consume a given version without hunting through the
 * experiments list.
 *
 * Empty array means "no experiment currently references this version" —
 * a useful signal that the row is orphaned and safe to archive.
 */
export type ReferencingExperimentRef = {
  id: string;
  name: string;
  status: ExperimentStatus;
};

/**
 * Public-facing PageVersion shape (subset relevant to slice 1 — the publish
 * workflow lives in slice 2 and will return more fields). `blocksOrderJson` is
 * untyped at the boundary; clients narrow it with the shared
 * `blocksOrderJson` schema if needed.
 *
 * `referencingExperiments` (issue #255 AC1): populated on EXPERIMENT-status
 * rows inside `experimentVersions[]` only. Other slots (latestDraft,
 * published, archived) leave this `undefined` — those statuses can't be
 * referenced by an experiment variant in the current model. The SPA reads
 * `?? []` defensively so the field's absence is identical to an empty list.
 */
export type PageVersionResponse = {
  id: string;
  pageId: string;
  versionNumber: number;
  status: ContentStatus;
  title: string;
  seoTitle: string | null;
  seoDescription: string | null;
  ogImageId: string | null;
  settingsJson: unknown;
  blocksOrderJson: unknown;
  createdFromVersionId: string | null;
  createdBy: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  referencingExperiments?: ReferencingExperimentRef[];
};

/**
 * Issue #283 — minimal ContentItem shape inlined on the page detail
 * response. Mirrors the public `ContentItemResponse` (defined in
 * `./content.ts`) but is duplicated here to avoid a `pages.ts → content.ts`
 * import cycle (content.ts already imports `ReferencingExperimentRef` from
 * pages.ts). Field set is intentionally identical so the picker can render
 * the ContentItem's name + blockType without a separate fetch.
 */
export type BlockPickerContentItem = {
  id: string;
  siteId: string;
  pageId: string | null;
  type: ContentItemType;
  blockType: BlockType | null;
  name: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Issue #283 — minimal traffic-ready ContentVersion shape for the per-block
 * picker. Only the fields the SPA needs to render the version dropdown
 * (id + versionNumber + status + publishedAt) are surfaced; the full
 * `ContentVersionResponse` shape (with `contentJson`, `judges`, etc.) is
 * out of scope here.
 *
 * "Traffic-ready" means `status IN ('PUBLISHED', 'EXPERIMENT')` per the
 * server-side variant validator's rule (`assertContentOverridesOnSite`,
 * #243). Surfacing only these statuses client-side prevents the operator
 * from selecting a DRAFT or ARCHIVED version that the server would later
 * reject with `VARIANT_TARGET_NOT_TRAFFIC_READY`.
 */
export type BlockPickerContentVersion = {
  id: string;
  contentItemId: string;
  versionNumber: number;
  status: ContentStatus;
  publishedAt: string | null;
};

/**
 * Issue #283 — one entry per block in the page's PageVersion
 * `blocksOrderJson.blocks[]`, carrying the referenced ContentItem and its
 * traffic-ready ContentVersions inline. Drives the per-block picker on
 * AddVariantDialog for page-scoped CONTENT_OVERRIDES experiments.
 *
 * Source of the blocks list:
 *   - `published` PageVersion's blocks if the page has one,
 *   - else `latestDraft` PageVersion's blocks (so the picker still works
 *     before the operator's first publish),
 *   - else `[]` if neither exists.
 *
 * `contentItem` is null when the block's `contentItemId` no longer resolves
 * to an active ContentItem on this site (orphan block reference). The SPA
 * renders these rows in a disabled / labelled-orphan state — the picker
 * cannot select an override against a missing item.
 *
 * `eligibleVersions` is empty when the ContentItem has no
 * PUBLISHED/EXPERIMENT versions yet. The SPA disables the row's version
 * select and shows a hint pointing at the ContentItem detail page so the
 * operator can publish a version for experiment use first.
 */
export type PageBlockWithEligibleVersions = {
  /**
   * Position of this block inside the source PageVersion's
   * `blocksOrderJson.blocks[]`. Stable so the SPA can key rows
   * deterministically when the same ContentItem appears more than once.
   */
  index: number;
  contentItemId: string;
  contentItem: BlockPickerContentItem | null;
  eligibleVersions: BlockPickerContentVersion[];
};

/**
 * Composite shape returned by `GET /api/admin/pages/:id` and by the create
 * endpoint. Includes the latest draft version (newest by versionNumber), the
 * currently-published version (if any, via the partial unique index on
 * `(pageId, status='PUBLISHED')`), and the page's archived versions ordered
 * newest-archived-first.
 *
 * `archived` is what makes unpublish recoverable: after an operator
 * unpublishes a version it transitions to ARCHIVED with `archivedAt` stamped,
 * and the UI binds the "Restore as draft" affordance off this array
 * (issue #186). On create the array is always empty.
 *
 * On create, `published` is always null because publish is a slice-2 concern;
 * `latestDraft` is always the freshly inserted v1 row.
 *
 * `blocksWithEligibleVersions` (issue #283): one entry per block in the
 * page's effective PageVersion (`published` if present, else `latestDraft`),
 * carrying the referenced ContentItem + its traffic-ready ContentVersions.
 * Drives the per-block picker on the AddVariantDialog for page-scoped
 * CONTENT_OVERRIDES experiments. Empty array when the page has no
 * PageVersion or its `blocksOrderJson.blocks[]` is empty. The SPA reads
 * `?? []` defensively so absence and emptiness are wire-shape-identical.
 */
export type PageWithVersionsResponse = {
  page: PageResponse;
  latestDraft: PageVersionResponse | null;
  published: PageVersionResponse | null;
  /**
   * EXPERIMENT-status versions (issue #243). Newest-first. Multiple may
   * coexist (the partial unique index that guards "one PUBLISHED per
   * page" deliberately does NOT cover EXPERIMENT, so a page can have
   * any number of experiment variants live at once). Each row is a
   * candidate target for the experiment-variant dropdown in the
   * experiment editor.
   */
  experimentVersions: PageVersionResponse[];
  archived: PageVersionResponse[];
  blocksWithEligibleVersions?: PageBlockWithEligibleVersions[];
};

// `blocksOrderJson` is exported by `./json.js` and re-exported via the
// barrel in `./index.ts`. Callers that need to validate or construct one
// at the API boundary should import it directly from there.

/**
 * Body schema for `PATCH /api/admin/page-versions/:id` (issue #15).
 *
 * Slice 2 only exposes `blocksOrderJson` and `title` updates on a draft
 * PageVersion. The full mutable surface (settingsJson, seoTitle, etc.) lands
 * in Slice 6 with the polished editor. Adding `blocksOrderJson` here is
 * load-bearing for issue #15 AC4: the save-time validator runs in the
 * service the moment a value lands on this endpoint.
 *
 * `.strict()` rejects unknown top-level keys so a typo surfaces as a 422
 * instead of being silently dropped. The schema also requires at least one
 * field to be present — empty PATCH bodies are rejected so the operator UI
 * always reflects a deliberate state change.
 */
export const updatePageVersionSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    blocksOrderJson: blocksOrderJson.optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  });
export type UpdatePageVersionInput = z.infer<typeof updatePageVersionSchema>;

// Re-export the shared blocksOrderJson type so callers that import from
// `pages.ts` don't need a second import.
export type { BlocksOrderJson };
